import { createContext, useState, useEffect, useContext, useMemo } from 'react'
import tokenUtils, { Environment } from './tokenUtils'

const ENDPOINT_ME = '/users/me/'
const ENDPOINT_LOGIN = '/rest-auth/login/'

export interface User {
  id: number
  email: string
  username: string
  name: string
  is_premium: boolean
  is_yearly_or_lifetime: boolean
  has_data_projects: boolean
  website: string
  location: string
  education: string
  plan: {
    name: string
    interval: string
  }
  subscription?: {
    is_status_temporarily_current: boolean
    current_period_end: string
    cancel_at_period_end: boolean
  }
  newsletters_subscription: boolean
  comments_subscription: boolean
  payment_in_progress: boolean
}

interface Credentials {
  email: string
  password: string
}

type AuthContextType = {
  user: User | null
  isAuthenticated: boolean
  isPremium: boolean
  login: (credentials: Credentials) => Promise<void>
  logout: () => void
  syncUser: () => Promise<void>
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export interface AuthProviderProps {
  children: React.ReactNode
  params: {
    APIUrl: string
    env?: Environment
  }
}

export function AuthProvider({ children, params }: AuthProviderProps) {
  const { APIUrl, env } = params
  const { getToken, deleteTokenCookie, saveTokenToCookie } = tokenUtils(env)
  const [user, setUser] = useState<User | null>(null)

  const isAuthenticated = !!user

  const isPremium = !!user?.is_premium

  const logout = () => {
    localStorage.clear()
    deleteTokenCookie()
    setUser(null)
  }

  const syncUser = async () => {
    const token = getToken()
    if (!token) {
      setUser(null)
      return
    }

    const res = await fetch(APIUrl.replace(/\/$/, '') + ENDPOINT_ME, {
      headers: {
        Authorization: `Token ${token}`,
      },
    })
    if (res.ok) {
      const data = await res.json()
      setUser(data)
    } else {
      logout()
    }
  }

  const login = async (credentials: Credentials) => {
    const res = await fetch(APIUrl.replace(/\/$/, '') + ENDPOINT_LOGIN, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(credentials),
    })

    const data: { key: string } | { non_field_errors: string[] } =
      await res.json()

    if (res.ok && 'key' in data) {
      saveTokenToCookie(data.key)
      await syncUser()
    }
    if ('non_field_errors' in data) {
      throw new Error(data.non_field_errors[0])
    }
  }

  useEffect(() => {
    syncUser()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const authValue: AuthContextType = useMemo(
    () => ({
      user,
      isAuthenticated,
      isPremium,
      login,
      logout,
      syncUser,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [user, isAuthenticated],
  )

  return (
    <AuthContext.Provider value={authValue}>{children}</AuthContext.Provider>
  )
}

export function useAuth() {
  const context = useContext(AuthContext)

  if (context === undefined) {
    throw new Error('useAuth must be within a AuthProvider component')
  }

  return context
}
